import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { AuthContext } from '../AuthGate';

// Roll Call — one table of everyone ONSITE (FC Associate I), with 6 columns
// that fill in as data arrives:
//   SOS Punch | SOS Scan | Break1 Scan | Lunch Punch | Lunch Scan | Break2 Scan
//
// Punches (SOS, Lunch) come from the attendance report. Scans (SOS, Break1,
// Lunch, Break2) come from manager badge scans. A scan is bucketed to the
// column whose planned time is CLOSEST to the scan time (e.g. 10:22 -> Break1,
// 13:26 -> Lunch, regardless of scan order).
//
// Planned times (5-min grace):
//   Day:   SOS 08:00, Break1 10:15, Lunch 13:15, Break2 16:15
//   Night: SOS 19:00, Break1 21:15, Lunch 00:15, Break2 03:15
// Early-punch highlight: a SOS/Lunch punch 6+ min before its planned time.
// Missing-scan highlight: only kicks in once >75% of onsite have that scan.

const SHIFT_TARGETS = {
  // minutes-of-day; night lunch/break2 are after midnight (+1440 handled below)
  day:   { sos: 8 * 60, break1: 10 * 60 + 15, lunch: 13 * 60 + 15, break2: 16 * 60 + 15 },
  night: { sos: 19 * 60, break1: 21 * 60 + 15, lunch: 24 * 60 + 15, break2: 27 * 60 + 15 },
};
// Hard display gates (minutes-of-day; night uses the +1440 post-midnight
// convention, same as hhmmToMin). A SOS/Lunch punch is shown ONLY if it falls
// inside its window; otherwise the cell is blanked. Rather show nothing than a
// wrong (e.g. night-shifter) time. Night Lunch 23:30-01:00 wraps midnight:
// 23:30 = 1410, 01:00 = 1500 (25:00).
const PUNCH_GATES = {
  day:   { sos: [6 * 60 + 30, 9 * 60],         lunch: [13 * 60, 14 * 60] },
  night: { sos: [18 * 60, 20 * 60],            lunch: [23 * 60 + 30, 25 * 60] },
};
const inGate = (min, gate) => min != null && gate && min >= gate[0] && min <= gate[1];
// Full shift-presence window (minutes-of-day; night uses the +1440 post-midnight
// convention, same as hhmmToMin). Used to decide whether an onsite record is
// actually working THIS shift vs. a stray/early punch that should not show as an
// empty roll-call row. Starts BEFORE SOS to cover normal pre-shift badge-ins
// (associates badge in ~15-60 min early; without this grace, someone who
// punched just before SOS was hidden and so couldn't be scanned). Day SOS=08:00
// -> window from 06:30; Night SOS=19:00 -> window from 18:00. (Day 06:30 still
// excludes the stray ~05:30 taps the off-shift filter is meant to drop.)
const SHIFT_WINDOW = {
  day:   [6 * 60 + 30, 18 * 60],            // 390 .. 1080  (06:30 .. 18:00)
  night: [18 * 60, 30 * 60 + 30],           // 1080 .. 1830 (18:00 .. 06:30 +1440)
};
// Is this onsite record genuinely present for `shift`? True if currently badged
// in, OR any punch falls inside the shift window, OR any manager scan exists.
// A single out-of-window punch with currentlyIn=false and no scans => not on
// this shift (don't render a blank row for them).
function presentForShift(o, shift, hasScan) {
  if (hasScan) return true;
  if (o && o.currentlyIn === true) return true;
  const win = SHIFT_WINDOW[shift];
  const list = Array.isArray(o && o.punchList) ? o.punchList : [];
  for (const p of list) {
    const m = hhmmToMin(p, shift);
    if (m != null && m >= win[0] && m <= win[1]) return true;
  }
  return false;
}
const COLS = [
  { key: 'sosPunch',    label: 'SOS Punch',    kind: 'punch', target: 'sos' },
  { key: 'sosScan',     label: 'SOS Scan',     kind: 'scan',  target: 'sos' },
  { key: 'break1Start', label: 'Break 1 Start', kind: 'scan', target: 'break1', optional: true },
  { key: 'break1Scan',  label: 'Break 1 End',  kind: 'scan',  target: 'break1' },
  { key: 'lunchPunch',  label: 'Lunch Punch',  kind: 'punch', target: 'lunch' },
  { key: 'lunchScan',   label: 'Lunch Scan',   kind: 'scan',  target: 'lunch' },
  { key: 'break2Start', label: 'Break 2 Start', kind: 'scan', target: 'break2', optional: true },
  { key: 'break2Scan',  label: 'Break 2 End',  kind: 'scan',  target: 'break2' },
];
const SCAN_COLS = ['sosScan', 'break1Scan', 'lunchScan', 'break2Scan'];
const EARLY_FLAG_MIN = 6;   // highlight (red) if 6+ min early
const LATE_FLAG_MIN = 15;   // highlight (yellow) if 15+ min late (late arrival)
const SCAN_GATE = 0.75;     // only flag missing scans once >75% scanned

function ptToday() {
  const pst = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  return `${pst.getFullYear()}-${String(pst.getMonth() + 1).padStart(2, '0')}-${String(pst.getDate()).padStart(2, '0')}`;
}
function ptShift() {
  const h = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })).getHours();
  return (h >= 6 && h < 19) ? 'day' : 'night';
}
// "HH:MM" -> minutes-of-day. For night shift, early-AM times (< ~06:00) are
// treated as next-day (+1440) so they sort/compare after the 19:00 start.
function hhmmToMin(hhmm, shift) {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
  const [h, m] = hhmm.split(':').map(Number);
  let mins = h * 60 + m;
  if (shift === 'night' && mins < 6 * 60) mins += 1440; // post-midnight
  return mins;
}
// ISO ts -> minutes-of-day in PT (for bucketing scans).
function tsToMinPT(ts, shift) {
  const d = new Date(ts);
  if (isNaN(d)) return null;
  const pt = new Date(d.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  let mins = pt.getHours() * 60 + pt.getMinutes();
  if (shift === 'night' && mins < 6 * 60) mins += 1440;
  return mins;
}
function fmtTs(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return ts;
  return d.toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: false });
}
// Which scan column is a scan-time closest to?
function nearestScanCol(scanMin, shift) {
  const t = SHIFT_TARGETS[shift];
  const opts = [['sosScan', t.sos], ['break1Scan', t.break1], ['lunchScan', t.lunch], ['break2Scan', t.break2]];
  let best = opts[0][0], bestD = Math.abs(scanMin - opts[0][1]);
  for (const [k, v] of opts) {
    const d = Math.abs(scanMin - v);
    if (d < bestD) { bestD = d; best = k; }
  }
  return best;
}

export default function RollCall({ api, site }) {
  const { user } = useContext(AuthContext) || {};
  const who = (user && user.username) || 'unknown';
  const [date, setDate] = useState(ptToday());
  const [shift, setShift] = useState(ptShift());
  const [onsite, setOnsite] = useState(null);
  const [roster, setRoster] = useState(null);
  const [scans, setScans] = useState([]);           // confirmed-from-store scans [{bb,ts,col}]
  const [queue, setQueue] = useState([]);            // locally-added, not yet saved [{bb,ts,col}]
  const [stu, setStu] = useState({});                // { "<empId>:<break>": {checked, by, at} }
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // --- Loaders -------------------------------------------------------------
  const loadOnsite = useCallback(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=ONSITE_${site}_${date}&shift_type=${shift}`)
      .then(r => r.json()).then(d => setOnsite(d && Array.isArray(d.onsite) ? d : null)).catch(() => setOnsite(null));
  }, [api, site, date, shift]);
  const loadRoster = useCallback(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=ROSTER_${site}&shift_type=data`)
      .then(r => r.json()).then(d => setRoster(d && d.bbToPerson ? d : null)).catch(() => setRoster(null));
  }, [api, site]);
  const loadScans = useCallback(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=ROLLCALL_${date}&shift_type=${shift}`)
      .then(r => r.json()).then(d => setScans(d && Array.isArray(d.scans) ? d.scans : [])).catch(() => setScans([]));
  }, [api, site, date, shift]);
  // STU Completed checkboxes live in their own cache item (ROLLCALL_STU_<date>)
  // so SWC-style whole-object writes never collide with the atomic scan-append.
  const loadStu = useCallback(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=ROLLCALL_STU_${date}&shift_type=${shift}`)
      .then(r => r.json()).then(d => setStu(d && d.stu ? d.stu : {})).catch(() => setStu({}));
  }, [api, site, date, shift]);

  useEffect(() => { loadOnsite(); loadRoster(); loadScans(); loadStu(); }, [loadOnsite, loadRoster, loadScans, loadStu]);
  useEffect(() => {
    const t = setInterval(() => { loadOnsite(); loadScans(); loadStu(); }, 30000);
    return () => clearInterval(t);
  }, [loadOnsite, loadScans, loadStu]);
  useEffect(() => { inputRef.current && inputRef.current.focus(); }, [date, shift]);

  // STU checkbox: record who/when, SWC-style whole-object PUT to the STU item.
  const toggleStu = async (empId, breakKey, isChecked) => {
    const id = `${empId}:${breakKey}`;
    const next = { ...stu };
    if (isChecked) next[id] = { checked: true, by: who, at: new Date().toISOString() };
    else delete next[id];
    setStu(next); // optimistic
    try {
      await fetch(`${api}/api/fclm/cache`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site, date: `ROLLCALL_STU_${date}`, shift_type: shift, data: { stu: next } }),
      });
    } catch (e) { loadStu(); }
  };

  // --- Scanning ------------------------------------------------------------
  // A scan is queued locally (optimistic) and auto-saved. Manual Save flushes
  // the whole queue too. Every scan is kept; nearest-time decides the column.
  const saveScans = useCallback((batch) => {
    if (!batch || batch.length === 0) return;
    setSaving(true);
    fetch(`${api}/api/fclm/scan-append`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: `ROLLCALL_${date}`, shift_type: shift, scans: batch }),
    })
      .then(r => r.json())
      .then(d => {
        if (d && d.success) {
          // Remove saved items from the queue (match by ts+bb), then reload truth.
          const savedKeys = new Set(batch.map(s => s.ts + '|' + s.bb));
          setQueue(q => q.filter(s => !savedKeys.has(s.ts + '|' + s.bb)));
          setStatus(`Saved ${d.appended || batch.length} scan${(d.appended || batch.length) === 1 ? '' : 's'} ✓`);
          loadScans();
        } else {
          setStatus('Save failed — scans kept locally, press Save to retry');
        }
      })
      .catch(() => setStatus('Save failed — scans kept locally, press Save to retry'))
      .finally(() => setSaving(false));
  }, [api, site, date, shift, loadScans]);

  const addScan = useCallback((bbRaw) => {
    const bb = String(bbRaw || '').trim();
    if (!bb) return;
    const ts = new Date().toISOString();
    const col = nearestScanCol(tsToMinPT(ts, shift), shift);
    const scan = { bb, ts, col };
    setQueue(q => [...q, scan]);
    // auto-save this one
    saveScans([scan]);
  }, [shift, saveScans]);

  const manualSave = () => { if (queue.length) saveScans(queue); else { loadScans(); setStatus('Nothing pending — reloaded.'); } };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addScan(e.target.value); e.target.value = ''; }
  };

  // --- Build the single onsite table --------------------------------------
  const model = useMemo(() => {
    const bbToPerson = roster?.bbToPerson || {};
    const empToBb = roster?.empToBb || {};
    const rosterLoaded = Object.keys(bbToPerson).length > 0;
    const fcaEmpIds = new Set(Object.keys(empToBb));
    const onsiteAll = onsite?.onsite || [];
    const onsiteList = rosterLoaded ? onsiteAll.filter(o => fcaEmpIds.has(o.empId)) : onsiteAll;

    const targets = SHIFT_TARGETS[shift];

    // All scans (confirmed + queued), resolved to employeeId via BB.
    const allScans = [...scans, ...queue];
    // empId -> { sosScan, break1Scan, lunchScan, break2Scan } (latest ts per col).
    // For the two break columns we ALSO track the earliest scan in the window as
    // the break START (break1Start/break2Start); the latest stays as the END
    // (break1Scan/break2Scan). Managers scan twice per break (out + back), so the
    // earlier scan in a break window is the start, the later is the return.
    const scanByEmp = {};
    const BREAK_START = { break1Scan: 'break1Start', break2Scan: 'break2Start' };
    const unmatched = {}; // bb -> {count, last, col}
    allScans.forEach(s => {
      const person = bbToPerson[s.bb];
      const col = s.col || nearestScanCol(tsToMinPT(s.ts, shift) ?? 0, shift);
      if (!person) {
        if (!unmatched[s.bb]) unmatched[s.bb] = { bb: s.bb, count: 0, last: s.ts, col };
        unmatched[s.bb].count += 1;
        if (s.ts > unmatched[s.bb].last) unmatched[s.bb].last = s.ts;
        return;
      }
      const e = person.empId;
      if (!scanByEmp[e]) scanByEmp[e] = {};
      const cur = scanByEmp[e][col];
      if (!cur || s.ts > cur) scanByEmp[e][col] = s.ts; // keep latest => break END / return
      // For break columns, also remember the EARLIEST scan in the window as START.
      const startKey = BREAK_START[col];
      if (startKey) {
        const curStart = scanByEmp[e][startKey];
        if (!curStart || s.ts < curStart) scanByEmp[e][startKey] = s.ts; // earliest => START
      }
    });
    // A single scan in a break window is ambiguous: keep it as the END (return,
    // matching the column's long-standing meaning). With only one scan we can't
    // observe the real start, so DERIVE it as END - 15 min (the standard break
    // length) rather than leaving it blank or equal to END.
    const BREAK_MS = 15 * 60 * 1000;
    Object.values(scanByEmp).forEach(m => {
      ['break1', 'break2'].forEach(b => {
        const startKey = `${b}Start`, endKey = `${b}Scan`;
        if (m[endKey] && (!m[startKey] || m[startKey] === m[endKey])) {
          m[startKey] = m[endKey] - BREAK_MS; // lone scan => start = end - 15 min
        }
      });
    });

    // Build each onsite row's 6 cells.
    const builtRows = onsiteList.map(o => {
      // Punches come LABELED from the backend (In/Out columns), so SOS and lunch
      // are unambiguous — no nearest-time guessing. Falls back to punchList for
      // any legacy cached record that predates the labeled parse.
      const mk = (hhmm) => hhmm ? { hhmm, min: hhmmToMin(hhmm, shift) } : null;
      let sosPunch = mk(o.sosPunch);
      // "Lunch Punch" = the lunch RETURN punch (In#2), which is what aligns with
      // the planned lunch time (e.g. 13:15) and the early/late highlights. The
      // lunch-OUT (Out#1, ~12:45) is when they LEFT and must NOT be shown here —
      // showing it made every punch-out look like a 30-min-early lunch punch.
      let lunchPunch = mk(o.lunchIn);
      if (!sosPunch && !o.sosPunch && Array.isArray(o.punchList) && o.punchList.length) {
        // Legacy record: approximate from the flat list.
        const ps = o.punchList.map(p => ({ hhmm: p, min: hhmmToMin(p, shift) })).filter(p => p.min != null);
        sosPunch = ps[0] || null;
        let ld = Infinity;
        ps.forEach(p => { const d = Math.abs(p.min - targets.lunch); if (d < ld) { ld = d; lunchPunch = p; } });
        if (lunchPunch && ld > 90) lunchPunch = null;
      }

      // HARD GATE: only display a SOS/Lunch punch if it falls inside the shift's
      // plausible window; otherwise blank it (a night-shifter's early-morning
      // punch, a stray, etc. simply shows nothing rather than a wrong time).
      const gates = PUNCH_GATES[shift];
      if (sosPunch && !inGate(sosPunch.min, gates.sos)) sosPunch = null;
      if (lunchPunch && !inGate(lunchPunch.min, gates.lunch)) lunchPunch = null;

      const sc = scanByEmp[o.empId] || {};
      const earlySos = sosPunch && sosPunch.min <= targets.sos - EARLY_FLAG_MIN;
      const earlyLunch = lunchPunch && lunchPunch.min <= targets.lunch - EARLY_FLAG_MIN;
      // Late arrival: first punch 15+ min AFTER plan (yellow). Late arrivals
      // legitimately won't have the on-time punch, so we don't also flag missing.
      const lateSos = sosPunch && sosPunch.min >= targets.sos + LATE_FLAG_MIN;
      const lateLunch = lunchPunch && lunchPunch.min >= targets.lunch + LATE_FLAG_MIN;
      const hasAnyScan = !!(sc.sosScan || sc.break1Scan || sc.lunchScan || sc.break2Scan);
      return {
        empId: o.empId, name: o.name, manager: o.manager,
        present: presentForShift(o, shift, hasAnyScan),
        // break-scan times (minutes-of-day) for the summary deltas
        break1ScanMin: sc.break1Scan ? tsToMinPT(sc.break1Scan, shift) : null,
        break2ScanMin: sc.break2Scan ? tsToMinPT(sc.break2Scan, shift) : null,
        break1ScanTs: sc.break1Scan || null,
        break2ScanTs: sc.break2Scan || null,
        cells: {
          sosPunch: sosPunch ? sosPunch.hhmm : '',
          sosScan: sc.sosScan ? fmtTs(sc.sosScan) : '',
          break1Start: sc.break1Start ? fmtTs(sc.break1Start) : '',
          break1Scan: sc.break1Scan ? fmtTs(sc.break1Scan) : '',
          lunchPunch: lunchPunch ? lunchPunch.hhmm : '',
          lunchScan: sc.lunchScan ? fmtTs(sc.lunchScan) : '',
          break2Start: sc.break2Start ? fmtTs(sc.break2Start) : '',
          break2Scan: sc.break2Scan ? fmtTs(sc.break2Scan) : '',
        },
        earlySos, earlyLunch, lateSos, lateLunch,
      };
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    // Robustly drop people who aren't actually on THIS shift: a stray/early
    // out-of-window punch, already clocked out, with no manager scans would
    // otherwise show as a completely empty row ("no scan in or out").
    const rows = builtRows.filter(r => r.present);
    const hiddenOffShift = builtRows.length - rows.length;

    // Per-scan-column coverage (for the 75% gate on missing-scan highlight).
    // Denominator excludes off-shift people (rows is already present-only) AND,
    // for each break, counts only associates still present at that break — using
    // the PUNCH as the reliable "still here" signal (SOS scanning is spottily
    // captured): Break 1 needs an SOS scan or SOS punch; Break 2 needs a Lunch
    // scan or Lunch (return) punch. This stops flex / half-day associates who
    // left before a later break from diluting that break's compliance, without
    // being thrown off by under-captured SOS scans. The scan is still the
    // compliance numerator; the punch only decides who's counted.
    const eligibleFor = (k, r) => {
      if (k === 'break1Scan') return !!(r.cells.sosScan || r.cells.sosPunch);
      if (k === 'break2Scan') return !!(r.cells.lunchScan || r.cells.lunchPunch);
      return true; // sosScan / lunchScan: everyone present
    };
    const coverage = {};
    SCAN_COLS.forEach(k => {
      const eligible = rows.filter(r => eligibleFor(k, r));
      const denom = eligible.length || 1;
      const have = eligible.filter(r => r.cells[k]).length;
      coverage[k] = { have, denom: eligible.length, pct: have / denom, gateOpen: have / denom > SCAN_GATE };
    });

    // Break summary: top 5 by delta (actual break scan - planned break), most
    // late first. Only associates with that break scan qualify.
    const buildBreak = (minKey, tsKey, target) => rows
      .filter(r => r[minKey] != null)
      .map(r => ({ empId: r.empId, name: r.name, scanTs: r[tsKey], scanMin: r[minKey], delta: r[minKey] - target }))
      .sort((a, b) => b.delta - a.delta)
      .slice(0, 5);
    const breakSummary = {
      first: buildBreak('break1ScanMin', 'break1ScanTs', targets.break1),
      second: buildBreak('break2ScanMin', 'break2ScanTs', targets.break2),
    };

    return { rows, coverage, breakSummary, unmatched: Object.values(unmatched), onsiteCount: rows.length, hiddenOffShift, rosterLoaded };
  }, [onsite, roster, scans, queue, shift]);

  // --- Styles --------------------------------------------------------------
  const cell = { border: '1px solid #ccc', padding: '3px 8px', fontSize: '0.78rem', textAlign: 'center', whiteSpace: 'nowrap' };
  const hdr = { ...cell, background: '#232f3e', color: '#fff', fontWeight: 600 };
  const nameCell = { ...cell, textAlign: 'left' };
  const planLabel = { sos: shift === 'night' ? '19:00' : '08:00', break1: shift === 'night' ? '21:15' : '10:15', lunch: shift === 'night' ? '00:15' : '13:15', break2: shift === 'night' ? '03:15' : '16:15' };
  // Per-column plan overrides: the break START columns plan 15 min before the
  // break END (break1Start 10:00 vs break1 End 10:15, etc.).
  const planByKey = {
    break1Start: shift === 'night' ? '21:00' : '10:00',
    break2Start: shift === 'night' ? '03:00' : '16:00',
  };

  return (
    <div>
      <h2 className="section-title">Roll Call — Onsite Scan Tracker (FC Associate I) — {site}</h2>

      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>Date: <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <label style={{ fontSize: '0.75rem' }}>Shift:{' '}
          <select value={shift} onChange={e => setShift(e.target.value)} style={{ fontSize: '0.75rem' }}>
            <option value="day">Day</option><option value="night">Night</option>
          </select>
        </label>
        <span style={{ fontSize: '0.75rem', color: '#444' }}>Seen this shift <strong>{model.onsiteCount}</strong>{onsite?.currentlyInCount != null && <> · currently in <strong>{onsite.currentlyInCount}</strong></>}</span>
        {model.hiddenOffShift > 0 && <span style={{ fontSize: '0.7rem', color: '#888' }} title="Onsite records with only a stray/out-of-window punch, already clocked out, and no manager scans — not working this shift.">· {model.hiddenOffShift} off-shift hidden</span>}
        {!model.rosterLoaded && <span style={{ fontSize: '0.7rem', color: '#c0392b' }}>⚠ Roster not loaded — badges can't match to names yet (runs once/day).</span>}
        {onsite?.fetched_at && <span style={{ fontSize: '0.65rem', color: '#888' }}>Onsite updated {fmtTs(onsite.fetched_at)} · auto-refresh 30s</span>}
      </div>

      {!onsite && (
        <div className="card" style={{ marginBottom: 8, background: '#fff8e1', border: '1px solid #f0c36d' }}>
          <span style={{ fontSize: '0.8rem', color: '#8a6d3b' }}>
            No onsite roster for <strong>{date} {shift}</strong> yet. The attendance sync only builds a shift's roster while that shift is running (every ~5 min), so a future or not-yet-started shift shows nothing here. Scans you record are still saved to this date/shift.
          </span>
        </div>
      )}

      {/* Scanner */}
      <div className="card" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Scan badge:</label>
        <input ref={inputRef} type="text" autoFocus placeholder="Scan or type a badge barcode, then Enter…"
          onKeyDown={onKeyDown} onBlur={e => {
            // Keep the scanner focused for the barcode gun, but DON'T steal focus
            // when the user is clicking another control (e.g. the Shift/Date picker).
            // Refocusing on those blurs closed the Shift dropdown before "Night"
            // could be selected.
            const to = e.relatedTarget;
            if (to && (to.tagName === 'SELECT' || to.tagName === 'INPUT' ||
                       to.tagName === 'BUTTON' || to.tagName === 'OPTION' ||
                       to.tagName === 'A')) return;
            setTimeout(() => {
              // Also bail if focus has since landed on an interactive control.
              const ae = document.activeElement;
              if (ae && ae !== document.body && ae !== inputRef.current &&
                  ['SELECT', 'INPUT', 'BUTTON', 'OPTION', 'A'].includes(ae.tagName)) return;
              inputRef.current && inputRef.current.focus();
            }, 0);
          }}
          style={{ fontSize: '1rem', padding: '8px 10px', width: 320, maxWidth: '100%', border: '2px solid #232f3e', borderRadius: 4 }} />
        <button onClick={manualSave} disabled={saving}
          style={{ fontSize: '0.8rem', padding: '8px 14px', background: queue.length ? '#f39c12' : '#232f3e', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          {saving ? 'Saving…' : queue.length ? `Save ${queue.length} pending` : 'Save / Reload'}
        </button>
        <span style={{ fontSize: '0.72rem', color: '#555' }}>
          Scan routed to nearest window (SOS {planLabel.sos} · Break {planLabel.break1} · Lunch {planLabel.lunch} · Break {planLabel.break2}).{status ? ` · ${status}` : ''}
        </span>
      </div>

      {/* Break summary — top 5 latest per break (delta = scan - planned) */}
      <div className="card" style={{ marginBottom: 8, overflowX: 'auto' }}>
        <h3 style={{ fontSize: '0.9rem', margin: '0 0 6px' }}>Greatest break-scan delay (top 5 per break) <span style={{ fontWeight: 400, color: '#888', fontSize: '0.72rem' }}>· signed in as <strong>{who}</strong></span></h3>
        {[
          { label: `First Break (plan ${planLabel.break1})`, bk: 'break1', list: model.breakSummary?.first || [] },
          { label: `Second Break (plan ${planLabel.break2})`, bk: 'break2', list: model.breakSummary?.second || [] },
        ].map(sec => (
          <div key={sec.bk} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, margin: '2px 0 4px' }}>{sec.label}</div>
            <table style={{ borderCollapse: 'collapse', minWidth: 420 }}>
              <thead><tr>
                <th style={hdr}>Associate</th><th style={hdr}>Break Scan</th><th style={hdr}>Delta</th><th style={hdr}>STU Completed</th>
              </tr></thead>
              <tbody>
                {sec.list.length === 0 ? (
                  <tr><td style={{ ...cell, textAlign: 'left', color: '#888' }} colSpan={4}>No {sec.bk === 'break1' ? 'first' : 'second'}-break scans yet.</td></tr>
                ) : sec.list.map(r => {
                  const id = `${r.empId}:${sec.bk}`;
                  const checked = !!(stu[id] && stu[id].checked);
                  return (
                    <tr key={id}>
                      <td style={{ ...cell, textAlign: 'left' }}>{r.name}</td>
                      <td style={cell}>{fmtTs(r.scanTs)}</td>
                      <td style={{ ...cell, color: r.delta > 0 ? '#c0392b' : '#27ae60', fontWeight: 700 }}>{r.delta > 0 ? `+${r.delta}` : r.delta} min</td>
                      <td style={cell}>
                        <input type="checkbox" checked={checked} onChange={e => toggleStu(r.empId, sec.bk, e.target.checked)} />
                        {checked && stu[id] && <span style={{ fontSize: '0.62rem', color: '#666', marginLeft: 6 }}>{stu[id].by} · {fmtTs(stu[id].at)}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* The one onsite table */}
      <div className="card" style={{ overflowX: 'auto' }}>
        {!onsite ? <p style={{ fontSize: '0.75rem', color: '#888' }}>No onsite data cached for {date} {shift} yet (syncs every ~5 min).</p> :
          model.rows.length === 0 ? <p style={{ fontSize: '0.8rem', color: '#888' }}>No onsite FC Associate I for {date} {shift}.</p> : (
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={hdr}>Associate</th>
                  <th style={hdr}>Manager</th>
                  {COLS.map(c => (
                    <th key={c.key} style={hdr}>{c.label}<div style={{ fontWeight: 400, fontSize: '0.62rem', opacity: 0.8 }}>plan {planByKey[c.key] || planLabel[c.target]}</div></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {model.rows.map(r => (
                  <tr key={r.empId}>
                    <td style={nameCell}>{r.name}</td>
                    <td style={{ ...nameCell, color: '#666' }}>{r.manager}</td>
                    {COLS.map(c => {
                      const val = r.cells[c.key];
                      let bg = '#fff', color = '#000', weight = 400;
                      if (c.kind === 'punch') {
                        // early-punch (red, 6+ min early) or late-arrival (yellow, 15+ min late)
                        const early = (c.key === 'sosPunch' && r.earlySos) || (c.key === 'lunchPunch' && r.earlyLunch);
                        const late = (c.key === 'sosPunch' && r.lateSos) || (c.key === 'lunchPunch' && r.lateLunch);
                        if (early) { bg = '#fdecea'; color = '#c0392b'; weight = 700; }
                        else if (late) { bg = '#fff3cd'; color = '#8a6d3b'; weight = 700; }
                      } else {
                        // scan column: green when present; red "missing" only
                        // after the 75% gate AND only for people still present at
                        // that break (scan or punch at the prior checkpoint).
                        const eligible = c.key === 'break1Scan' ? !!(r.cells.sosScan || r.cells.sosPunch)
                                       : c.key === 'break2Scan' ? !!(r.cells.lunchScan || r.cells.lunchPunch)
                                       : true;
                        if (val) { bg = '#eafaf1'; }
                        else if (eligible && model.coverage[c.key] && model.coverage[c.key].gateOpen) { bg = '#fdecea'; }
                      }
                      return <td key={c.key} style={{ ...cell, background: bg, color, fontWeight: weight }}>{val || (c.kind === 'scan' && (c.key === 'break1Scan' ? !!(r.cells.sosScan || r.cells.sosPunch) : c.key === 'break2Scan' ? !!(r.cells.lunchScan || r.cells.lunchPunch) : true) && model.coverage[c.key] && model.coverage[c.key].gateOpen ? '—' : '')}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {/* coverage footer */}
        {model.rows.length > 0 && (
          <div style={{ fontSize: '0.68rem', color: '#666', marginTop: 6 }}>
            Scan coverage: {SCAN_COLS.map(k => `${COLS.find(c => c.key === k).label.replace(/ (Scan|End)$/, '')} ${Math.round((model.coverage[k]?.pct || 0) * 100)}% (of ${model.coverage[k]?.denom ?? 0})`).join(' · ')}
            {' '}<span style={{ color: '#888' }}>(missing-scan highlight starts above 75%; breaks count only associates still present at that checkpoint — SOS scan/punch before Break 1, Lunch scan/punch before Break 2)</span>
          </div>
        )}
      </div>

      {/* unmatched badges — nothing lost */}
      {model.unmatched.length > 0 && (
        <div className="card" style={{ marginTop: 8, overflowX: 'auto' }}>
          <h3 style={{ fontSize: '0.85rem', margin: '0 0 6px' }}>Scanned — unmatched badge ({model.unmatched.length})</h3>
          <table style={{ borderCollapse: 'collapse' }}>
            <thead><tr><th style={hdr}>Badge (BB)</th><th style={hdr}>Latest Scan</th><th style={hdr}>Scans</th></tr></thead>
            <tbody>
              {model.unmatched.map(u => (
                <tr key={u.bb} style={{ background: '#f3e5f5' }}>
                  <td style={cell}>{u.bb}</td><td style={cell}>{fmtTs(u.last)}</td><td style={cell}>{u.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
